import { useDispatch, useSelector } from 'react-redux';
import useAlerts from '../useAlerts';
import useLoading from '../useLoading';
import useModals from '../useModals';
import useConfirmModal from '../useConfirmModal';
import { RootState } from '../../store/store';
import {
  setProductToEdit,
  clearProductToEdit,
  closeAddProductModal,
  closeEditProductModal,
} from '../../store/slices/purchases/taxCreditPurchaseSlice';
import { useBasePurchase } from './useBasePurchase';
import TaxCreditPurchaseService from '../../services/purchases/taxCreditService';
import { updateTaxCreditTotals } from '../../store/slices/purchases/basePurchaseSlice';

export const useTaxCreditPurchase = () => {
  const dispatch = useDispatch();
  const { show: showAlert } = useAlerts();
  const { openModal } = useModals();
  const confirm = useConfirmModal();
  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
  const { addProduct, updateProduct, deleteProduct } = useBasePurchase();

  const { productToEdit, showAddProductModal, showEditProductModal } = useSelector(
    (state: RootState) => state.taxCreditPurchase
  );

  const handlerAddProduct = async (product: any) => {
    try {
      showLoadingIndicator();
      const response = await TaxCreditPurchaseService.addProduct(product);
      // console.log(response);

      if (response.statusCode === 201) {
        const { title, message, type, data } = response;

        const { product: savedProduct, totals } = data;

        addProduct(savedProduct); // ✅ Agrega producto al estado
        dispatch(updateTaxCreditTotals(totals)); // ✅ Agrega totales al estado

        dispatch(closeAddProductModal()); // Cierra modal
        openModal(type, { title, message }); 
      } else {
        showAlert('error', 'Error', 'No se pudo agregar el producto.');
      }
    } catch (error) {
      console.error('❌ Error al agregar producto:', error);
      showAlert('error', 'Error', 'Ocurrió un error inesperado.');
    } finally {
      hideLoadingIndicator();
    }
  };


  const handlerUpdateProduct = async (product: any) => {
    try {
      showLoadingIndicator();
      const response = await TaxCreditPurchaseService.updateProduct(product);

      if (response.statusCode === 200) {
        const { title, message, type, data } = response;

        // ✅ Extraer producto y totales
        const { product: updatedProduct, totals } = data;

        // ✅ Actualizar producto y totales en el estado global
        updateProduct(updatedProduct);
        dispatch(updateTaxCreditTotals(totals));

        // ✅ Cerrar modal y mostrar mensaje
        dispatch(closeEditProductModal());
        openModal(type, { title, message });
      } else {
        showAlert('error', 'Error', 'No se pudo actualizar el producto.');
      }
    } catch (error) {
      console.error('❌ Error al actualizar producto:', error);
      showAlert('error', 'Error', 'Ocurrió un error inesperado.');
    } finally {
      hideLoadingIndicator();
    }
  };


  const handlerDeleteProduct = async (id: number) => {
    confirm({
      title: '¿Eliminar Producto?',
      message: '¿Estás seguro que deseas eliminar el producto?',
      icon: 'warning',
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      confirmArgs: id,
      onConfirm: async (id) => {
        try {
          showLoadingIndicator();
          const response = await TaxCreditPurchaseService.deleteProduct(id);
          console.log(response);
          if (response.statusCode === 200) {
            const { title, message, type, data } = response;

            // ✅ Eliminar del estado local
            deleteProduct(id);

            // ✅ Actualizar los totales globales
            dispatch(updateTaxCreditTotals(data.totals));

            openModal(type, { title, message }); 
          } else {
            showAlert('error', 'Error', 'No se pudo eliminar el producto.');
          }
        } catch (error) {
          console.error('❌ Error al eliminar producto:', error);
          showAlert('error', 'Error', 'Ocurrió un error inesperado.');
        } finally {
          hideLoadingIndicator();
        }
      },
    });
  };


  const handlerRecalculateTotals = async (purchaseId: number) => {
    try {
      showLoadingIndicator();
      const response = await TaxCreditPurchaseService.recalculateTotals(purchaseId);

      if (response.statusCode === 200) {
        const { title, message, type, data } = response;

        dispatch(updateTaxCreditTotals(data)); // ✅ Actualiza los totales en el estado global

        openModal(type, { title, message });
      } else {
        showAlert('error', 'Error', 'No se pudieron recalcular los totales.');
      }
    } catch (error) {
      console.error('❌ Error al recalcular totales:', error);
      showAlert('error', 'Error', 'Ocurrió un error inesperado.');
    } finally {
      hideLoadingIndicator();
    }
  };

  const handlerUpdatePerceivedIVA = async (purchaseId: number, perceivedIVA: number) => {
    try {
      showLoadingIndicator();
      const response = await TaxCreditPurchaseService.updatePerceivedIVA(purchaseId, perceivedIVA);

      if (response.statusCode === 200) {
        const { title, message, type, data } = response;

        dispatch(updateTaxCreditTotals(data)); // ✅ Actualiza los totales en el estado global

        openModal(type, { title, message });
        return true;
      } else {
        showAlert('error', 'Error', 'No se pudo actualizar el IVA Percibido.');
        return false;
      }
    } catch (error: any) {
      console.error('❌ Error al actualizar IVA Percibido:', error);
      const message =
        error?.response?.data?.message || 'Ocurrió un error inesperado.';
      showAlert('error', 'Error', message);
      return false;
    } finally {
      hideLoadingIndicator();
    }
  };

  const handlerGetProductById = async (detailId: number) => {
    try {
      showLoadingIndicator();
      const response = await TaxCreditPurchaseService.getProductById(detailId);
      console.log(response);
      if (response.statusCode === 200) {
        dispatch(setProductToEdit(response.data));
        return response.data;
      } else {
        showAlert('error', 'Error', 'No se pudo obtener el producto.');
      }
    } catch (error) {
      console.error('❌ Error al obtener producto:', error);
      showAlert('error', 'Error', 'Ocurrió un error inesperado.');
    } finally {
      hideLoadingIndicator();
    }
  };

  return {
    handlerAddProduct,
    handlerUpdateProduct,
    handlerDeleteProduct,
    handlerGetProductById,
    handlerRecalculateTotals,
    handlerUpdatePerceivedIVA,
    productToEdit,
    clearProductToEdit,
    showAddProductModal,
    showEditProductModal,
  };
};
